import { useEffect, useRef, type MutableRefObject } from 'react'

import { useStudioStore } from '@/store/studio-store'
import {
  PROJECT_CHAT_THREAD_RETRY_EVENT,
  readProjectChatThread,
  retireProjectChatThreadRetry,
  type ProjectChatPrimaryRoute,
  type ProjectChatThreadRetryRequest,
  type ProjectChatThreadScope,
} from '../project-chat-thread'

export interface ProjectChatThreadRetryExecution {
  scope: ProjectChatThreadScope
  sourceOutcomeId: string
  route: ProjectChatPrimaryRoute
  intent: string
}

interface UseProjectChatThreadRetryHandlerInput {
  enabled?: boolean
  busy: boolean
  isBusy?: () => boolean
  submissionInFlightRef: MutableRefObject<boolean>
  submit: (execution: ProjectChatThreadRetryExecution) => Promise<void> | void
}

function currentProjectChatThreadScope(): ProjectChatThreadScope | null {
  const studio = useStudioStore.getState()
  const userId = studio.session?.user?.id ?? null
  const projectId = studio.currentProjectId ?? null
  return userId && projectId ? { userId, projectId } : null
}

function sameProjectChatThreadScope(
  left: ProjectChatThreadScope | null,
  right: ProjectChatThreadScope | null,
): boolean {
  if (!left || !right) return left === right
  return left.userId === right.userId && left.projectId === right.projectId
}

/**
 * Mount the single durable Retry event boundary shared by build and edit Chat
 * shells. The persisted outcome owns route and intent authority; event detail
 * only identifies the durable record to replay and retire after acceptance.
 */
export function useProjectChatThreadRetryHandler({
  enabled = true,
  busy,
  isBusy = () => false,
  submissionInFlightRef,
  submit,
}: UseProjectChatThreadRetryHandlerInput): void {
  const busyRef = useRef(busy)
  const isBusyRef = useRef(isBusy)
  const submitRef = useRef(submit)
  busyRef.current = busy
  isBusyRef.current = isBusy
  submitRef.current = submit

  useEffect(() => {
    if (!enabled) return

    const retryPersistedTurn = (event: Event) => {
      const { scope, sourceOutcomeId } = (
        event as CustomEvent<ProjectChatThreadRetryRequest>
      ).detail
      if (
        !sameProjectChatThreadScope(scope, currentProjectChatThreadScope()) ||
        busyRef.current ||
        isBusyRef.current() ||
        submissionInFlightRef.current
      ) {
        return
      }
      const persistedOutcome = readProjectChatThread(scope)?.turns.find(
        (turn) => turn.kind === 'outcome' && turn.id === sourceOutcomeId,
      )
      if (
        !persistedOutcome ||
        persistedOutcome.kind !== 'outcome' ||
        !persistedOutcome.retry.retryable
      ) {
        return
      }
      const persistedRetry = persistedOutcome.retry
      submissionInFlightRef.current = true
      void Promise.resolve()
        .then(() =>
          submitRef.current({
            scope,
            sourceOutcomeId,
            route: persistedRetry.route,
            intent: persistedRetry.intent,
          }),
        )
        .then(
          () => {
            const retired = retireProjectChatThreadRetry(
              scope,
              sourceOutcomeId,
              Date.now(),
            )
            const retiredOutcome = retired?.turns.find(
              (turn) => turn.kind === 'outcome' && turn.id === sourceOutcomeId,
            )
            if (!retiredOutcome || retiredOutcome.kind !== 'outcome') return
            useStudioStore.setState({
              chatMessages: useStudioStore
                .getState()
                .chatMessages.map((message) =>
                  message.kind === 'project-chat-outcome' &&
                  message.id === sourceOutcomeId
                    ? { ...retiredOutcome, kind: 'project-chat-outcome' }
                    : message,
                ),
            })
          },
          () => {
            // A rejected replay did not replace the failed result, so the
            // durable source remains the user's recovery authority.
          },
        )
        .finally(() => {
          // Give React one commit turn to publish the route-specific busy state
          // before reopening the synchronous admission window.
          window.setTimeout(() => {
            submissionInFlightRef.current = false
          }, 0)
        })
    }

    window.addEventListener(PROJECT_CHAT_THREAD_RETRY_EVENT, retryPersistedTurn)
    return () =>
      window.removeEventListener(
        PROJECT_CHAT_THREAD_RETRY_EVENT,
        retryPersistedTurn,
      )
  }, [enabled, submissionInFlightRef])
}
